/**
 * @fileoverview topology.ts — the pure topology engine.
 * @module @liquidefy/network-deployment/topology
 * @license LQD Research LLC
 *
 * @author Scott Martin
 * @since 1.0.0 (n3)
 * @tier Infrastructure
 *
 * Validates Liquidefy's declared desired state and produces the
 * DAG order the substrate `wave` sequencer consumes. Pure — no fs
 * / env / network / ambient. CONTROL-plane (browser-safe): you can
 * preview/validate a topology in a dashboard or Lyn node. Plan §7.
 */

import type {
  LiquidefyTopology,
  TopologyValidation,
  TargetNode,
} from "./topology-types.js";

/**
 * Validate structure + the target DAG. Returns `ok:false` with
 * human-readable `errors` (cycles, dangling deps, selection
 * mismatches, env/policy references) — never throws for a bad
 * declaration (that is data, validated). `order` is a Kahn
 * topological sort (deploy order) iff `ok`.
 */
export function validateTopology(
  t: LiquidefyTopology,
): TopologyValidation {
  const errors: string[] = [];
  const envIds = new Set(t.environments.map((e) => e.id));
  const targetIds = new Set(t.targets.map((x) => x.id));

  if (t.targets.length === 0) errors.push("topology has no targets");
  if (targetIds.size !== t.targets.length) {
    errors.push("duplicate target ids");
  }

  // Selection coverage: every target's connector must be selected
  // for its face.
  for (const tg of t.targets) {
    const forFace = t.selection.byFace[tg.face];
    if (!forFace || !forFace.includes(tg.connectorId)) {
      errors.push(
        `target "${tg.id}": connector "${tg.connectorId}" not in ` +
          `selection for face "${tg.face}"`,
      );
    }
    for (const dep of tg.dependsOn) {
      if (!targetIds.has(dep)) {
        errors.push(`target "${tg.id}" depends on unknown "${dep}"`);
      }
    }
  }

  // Policy references must point at declared environments.
  for (const e of t.gates.requireApproval) {
    if (!envIds.has(e)) {
      errors.push(`gates.requireApproval references unknown env "${e}"`);
    }
  }
  for (const e of Object.keys(t.gates.budgetCeiling)) {
    if (!envIds.has(e)) {
      errors.push(`gates.budgetCeiling references unknown env "${e}"`);
    }
  }
  for (const e of t.promotion.chain) {
    if (!envIds.has(e)) {
      errors.push(`promotion.chain references unknown env "${e}"`);
    }
  }

  // k11 (hop 1) — OPTIONAL database-deploy policy. Pure structural
  // validation: every policy key must name a DECLARED target whose
  // face is "database"; refs/retention must be well-formed. Absent
  // ⇒ this block is skipped (byte-identical back-compat).
  if (t.database) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.database.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`database policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "database") {
        errors.push(
          `database policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "database"`,
        );
      }
      if (!pol.migrationSetRef) {
        errors.push(`database policy "${tid}": migrationSetRef is empty`);
      }
      if (!Number.isFinite(pol.snapshotRetention) || pol.snapshotRetention < 0) {
        errors.push(
          `database policy "${tid}": snapshotRetention must be >= 0`,
        );
      }
    }
  }

  // L12 (hop 1) — OPTIONAL storage-deploy policy. Pure structural
  // validation: every policy key must name a DECLARED target whose
  // face is "storage"; providerRef must be non-empty; retention
  // >= 0. Absent ⇒ this block is skipped (byte-identical).
  if (t.storage) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.storage.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`storage policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "storage") {
        errors.push(
          `storage policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "storage"`,
        );
      }
      if (!pol.providerRef) {
        errors.push(`storage policy "${tid}": providerRef is empty`);
      }
      if (
        !Number.isFinite(pol.snapshotRetention) ||
        pol.snapshotRetention < 0
      ) {
        errors.push(
          `storage policy "${tid}": snapshotRetention must be >= 0`,
        );
      }
    }
  }

  // m12 (hop 1) — OPTIONAL application-release policy. Pure
  // structural validation: every key names a DECLARED target
  // whose face is "release"; imageRef non-empty; replicas >= 1.
  // Absent ⇒ skipped (byte-identical).
  if (t.release) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.release.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`release policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "release") {
        errors.push(
          `release policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "release"`,
        );
      }
      if (!pol.imageRef) {
        errors.push(`release policy "${tid}": imageRef is empty`);
      }
      if (!Number.isInteger(pol.replicas) || pol.replicas < 1) {
        errors.push(
          `release policy "${tid}": replicas must be an integer >= 1`,
        );
      }
    }
  }

  // n12 (hop 1) — OPTIONAL traffic/DNS/TLS policy. Pure structural
  // validation: every key names a DECLARED target whose face is
  // "traffic"; domain non-empty; certMinTtlMs >= 1. Absent ⇒
  // skipped (byte-identical).
  if (t.traffic) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.traffic.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`traffic policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "traffic") {
        errors.push(
          `traffic policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "traffic"`,
        );
      }
      if (!pol.domain) {
        errors.push(`traffic policy "${tid}": domain is empty`);
      }
      if (!Number.isInteger(pol.certMinTtlMs) || pol.certMinTtlMs < 1) {
        errors.push(
          `traffic policy "${tid}": certMinTtlMs must be an integer >= 1`,
        );
      }
    }
  }

  // o11 (hop 1) — OPTIONAL app-cache policy. Pure structural
  // validation: every key names a DECLARED target whose face is
  // "cache"; namespace non-empty; maxEntries>=1; ttlMs>=1. Absent
  // ⇒ skipped (byte-identical).
  if (t.cache) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.cache.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`cache policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "cache") {
        errors.push(
          `cache policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "cache"`,
        );
      }
      if (!pol.namespace) {
        errors.push(`cache policy "${tid}": namespace is empty`);
      }
      if (!Number.isInteger(pol.maxEntries) || pol.maxEntries < 1) {
        errors.push(
          `cache policy "${tid}": maxEntries must be an integer >= 1`,
        );
      }
      if (!Number.isInteger(pol.ttlMs) || pol.ttlMs < 1) {
        errors.push(
          `cache policy "${tid}": ttlMs must be an integer >= 1`,
        );
      }
    }
  }

  // p11 (hop 1) — OPTIONAL app-queue policy. Pure structural
  // validation: every key names a DECLARED target whose face is
  // "queue"; topic non-empty; maxDepth>=1; maxAgeMs>=1. Absent ⇒
  // skipped (byte-identical).
  if (t.queue) {
    const byFace = new Map(t.targets.map((x) => [x.id, x.face]));
    for (const [tid, pol] of Object.entries(t.queue.byTarget)) {
      if (!targetIds.has(tid)) {
        errors.push(`queue policy references unknown target "${tid}"`);
      } else if (byFace.get(tid) !== "queue") {
        errors.push(
          `queue policy target "${tid}" is face ` +
            `"${byFace.get(tid)}", expected "queue"`,
        );
      }
      if (!pol.topic) {
        errors.push(`queue policy "${tid}": topic is empty`);
      }
      if (!Number.isInteger(pol.maxDepth) || pol.maxDepth < 1) {
        errors.push(
          `queue policy "${tid}": maxDepth must be an integer >= 1`,
        );
      }
      if (!Number.isInteger(pol.maxAgeMs) || pol.maxAgeMs < 1) {
        errors.push(
          `queue policy "${tid}": maxAgeMs must be an integer >= 1`,
        );
      }
    }
  }

  // Kahn topo-sort over the target DAG (also = cycle detector).
  const order: string[] = [];
  if (errors.length === 0) {
    const indeg = new Map<string, number>();
    const out = new Map<string, string[]>();
    for (const tg of t.targets) {
      indeg.set(tg.id, tg.dependsOn.length);
      for (const dep of tg.dependsOn) {
        (out.get(dep) ?? out.set(dep, []).get(dep)!).push(tg.id);
      }
    }
    const queue = t.targets
      .filter((tg) => (indeg.get(tg.id) ?? 0) === 0)
      .map((tg) => tg.id);
    while (queue.length > 0) {
      const id = queue.shift()!;
      order.push(id);
      for (const nxt of out.get(id) ?? []) {
        const d = (indeg.get(nxt) ?? 0) - 1;
        indeg.set(nxt, d);
        if (d === 0) queue.push(nxt);
      }
    }
    if (order.length !== t.targets.length) {
      const unresolved = t.targets
        .map((x) => x.id)
        .filter((id) => !order.includes(id));
      errors.push(
        `target DAG has a cycle (unresolved: ${unresolved.join(", ")})`,
      );
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    order: errors.length === 0 ? order : [],
  };
}

function targetKey(n: TargetNode): string {
  return `${n.id}|${n.face}|${n.connectorId}|${[...n.dependsOn].sort().join(",")}`;
}

/**
 * Human-readable desired-state diff (next vs prior). Drives the
 * `apply`/preview surface — what *would* change.
 */
export function diffTopology(
  next: LiquidefyTopology,
  prev?: LiquidefyTopology,
): readonly string[] {
  if (!prev) return ["+ initial topology (no prior state)"];
  const out: string[] = [];

  const pById = new Map(prev.targets.map((x) => [x.id, x]));
  const nById = new Map(next.targets.map((x) => [x.id, x]));
  for (const [id, n] of nById) {
    const p = pById.get(id);
    if (!p) out.push(`+ target ${id} (${n.face}/${n.connectorId})`);
    else if (targetKey(p) !== targetKey(n)) out.push(`~ target ${id}`);
  }
  for (const id of pById.keys()) {
    if (!nById.has(id)) out.push(`- target ${id}`);
  }

  if (
    JSON.stringify(prev.selection) !== JSON.stringify(next.selection)
  ) {
    out.push("~ provider selection changed");
  }
  if (JSON.stringify(prev.gates) !== JSON.stringify(next.gates)) {
    out.push("~ gate policy changed");
  }
  if (
    JSON.stringify(prev.promotion) !== JSON.stringify(next.promotion)
  ) {
    out.push("~ promotion policy changed");
  }
  if (
    JSON.stringify(prev.ephemeral) !== JSON.stringify(next.ephemeral)
  ) {
    out.push("~ ephemeral policy changed");
  }
  if (
    JSON.stringify(prev.database ?? null) !==
    JSON.stringify(next.database ?? null)
  ) {
    out.push("~ database deploy policy changed");
  }
  if (
    JSON.stringify(prev.storage ?? null) !==
    JSON.stringify(next.storage ?? null)
  ) {
    out.push("~ storage deploy policy changed");
  }
  if (
    JSON.stringify(prev.release ?? null) !==
    JSON.stringify(next.release ?? null)
  ) {
    out.push("~ release deploy policy changed");
  }
  if (
    JSON.stringify(prev.traffic ?? null) !==
    JSON.stringify(next.traffic ?? null)
  ) {
    out.push("~ traffic deploy policy changed");
  }
  if (
    JSON.stringify(prev.cache ?? null) !==
    JSON.stringify(next.cache ?? null)
  ) {
    out.push("~ cache deploy policy changed");
  }
  if (
    JSON.stringify(prev.queue ?? null) !==
    JSON.stringify(next.queue ?? null)
  ) {
    out.push("~ queue deploy policy changed");
  }
  return out.length > 0 ? out : ["= no changes"];
}
